import React, { useState, useEffect } from 'react';
import { getEmployees } from '../services/attendanceApi';
import { isMobileDevice } from '../utils/deviceDetection';
import { logDebug } from '../utils/logger';
import { perfStart, perfEnd } from '../utils/performance';
import './LoginPage.css';

const normalizeEmail = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\u00A0/g, ' ')
    .trim();

const normalizeStatus = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const isActiveEmployeeStatus = (status) => {
  const normalized = normalizeStatus(status);
  if (!normalized) return true;
  return (
    normalized === 'dang lam viec' ||
    normalized === 'dang hoat dong' ||
    normalized === 'hoat dong' ||
    normalized === 'active' ||
    normalized === 'working'
  );
};

const isInactiveEmployeeStatus = (status) => {
  const normalized = normalizeStatus(status);
  if (!normalized) return false;
  return (
    normalized.includes('nghi viec') ||
    normalized.includes('ngung hoat dong') ||
    normalized === 'inactive' ||
    normalized === 'disabled' ||
    normalized === 'terminated'
  );
};

function LoginPage({ onLogin, onAdminLogin }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const isMobile = isMobileDevice();
    const isDesktopDevice = !isMobile;
    logDebug('[LoginPage] Device check - isMobile:', isMobile, 'isDesktop:', isDesktopDevice);
    setIsDesktop(isDesktopDevice);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    const loginEmail = normalizeEmail(email);
    if (!loginEmail) {
      setError('Vui lòng nhập email');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(loginEmail)) {
      setError('Email không hợp lệ');
      return;
    }

    setLoading(true);
    setError('');
    perfStart('login_submit');

    try {
      const employees = await getEmployees(true);
      if (!Array.isArray(employees) || employees.length === 0) {
        setError('Hệ thống chưa có dữ liệu nhân viên trên bản deploy này');
        return;
      }
      const employee = employees.find(
        (emp) => normalizeEmail(emp.email) === loginEmail
      );

      if (employee) {
        if (isInactiveEmployeeStatus(employee.status) && !isActiveEmployeeStatus(employee.status)) {
          setError('Nhân viên này đã nghỉ việc hoặc ngừng hoạt động');
        } else {
          onLogin(employee);
        }
      } else {
        setError('Email không tồn tại trong hệ thống');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Lỗi kết nối: ' + (err.message || 'Không thể kiểm tra email'));
    } finally {
      perfEnd('login_submit');
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          <img src="/logo.png" alt="FSI Logo" className="login-logo" />
          <h1>Chấm Công</h1>
          <p>Nhập email để bắt đầu</p>
        </div>

        {isDesktop && (
          <div className="desktop-warning-box">
            <div className="warning-icon-small">!</div>
            <p className="warning-text">
              <strong>Ứng dụng chỉ dành cho điện thoại di động.</strong>
            </p>
            <p className="warning-hint">
              Vui lòng sử dụng điện thoại để chấm công. Quản trị viên có thể truy cập trang quản trị trên máy tính.
            </p>
          </div>
        )}

        <form onSubmit={handleLogin} className="login-form">
          <div className="form-group">
            <input
              type="email"
              placeholder="Email (Ví dụ: nhanvien@example.com)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading || isDesktop}
              autoFocus={!isDesktop}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            type="submit"
            className="btn-login"
            disabled={loading || isDesktop}
            title={isDesktop ? 'Vui lòng sử dụng điện thoại để chấm công' : ''}
          >
            {loading ? 'Đang kiểm tra...' : 'Bắt Đầu Chấm Công'}
          </button>
        </form>

        <div className="login-footer">
          <button onClick={onAdminLogin} className="btn-admin-link">
            Quản trị viên
          </button>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
